'use client'

import {
  type Dispatch,
  type FC,
  type ReactNode,
  type RefObject,
  type SetStateAction,
  createContext,
  useState,
  useContext,
  useRef,
  useCallback,
  useEffect,
} from 'react'
import { scrollToSection, hapticTap } from '@/lib/utils'
import { fetchCurrentUser } from '@/lib/auth/fetch-current-user'
import { submitForm } from '@/lib/actions/submit-form'
import { trackFormDealClaim } from '@/lib/analytics'
import { useBreakpoint } from '@/hooks/useBreakpoint'

type DealClaimOverrides = {
  dealId?: number | null
  appName?: string
  claimValueCents?: number | null
}

type DealContextType = {
  claimed: boolean
  setClaimed: Dispatch<SetStateAction<boolean>>
  triggerClaim: boolean
  setTriggerClaim: Dispatch<SetStateAction<boolean>>
  dealSelectionComplete: boolean
  setDealSelectionComplete: Dispatch<SetStateAction<boolean>>
  emailInputRef: RefObject<{ focus: () => void } | null>
  email: string
  setEmail: (email: string) => void
  dealId: number | null
  setDealId: Dispatch<SetStateAction<number | null>>
  appName: string
  setAppName: Dispatch<SetStateAction<string>>
  claimValueCents: number | null
  setClaimValueCents: Dispatch<SetStateAction<number | null>>
  emailIsRequired: boolean
  setEmailIsRequired: Dispatch<SetStateAction<boolean>>
  desktopIsRequired: boolean
  setDesktopIsRequired: Dispatch<SetStateAction<boolean>>
  showDesktopSheet: boolean
  setShowDesktopSheet: Dispatch<SetStateAction<boolean>>
  dealTracked: boolean
  setDealTracked: Dispatch<SetStateAction<boolean>>
  signIn: boolean
  setSignIn: Dispatch<SetStateAction<boolean>>
  response: 'error' | null
  handleClaim: (
    buttonOnly?: boolean,
    emailRequired?: boolean | null | undefined,
    desktopIsRequired?: boolean | null | undefined,
    overrides?: DealClaimOverrides,
  ) => Promise<boolean>
  handleClick: () => void
}

// Read the desktop viewport synchronously at claim time. Deliberately NOT
// useBreakpoint's `lg`: useMediaQuery uses useSyncExternalStore with a server
// snapshot of `false`, so `lg` reads false on the first client render and only
// flips true after. The claim is one-shot — ?claim=true calls handleClaim once
// on mount (DealContainer) and the button path consumes triggerClaim — so a
// claim that fires during that window would wrongly show the desktop handoff
// sheet on a real desktop and never retry, blocking the confetti. matchMedia
// reflects the true viewport at call time regardless of React's snapshot.
// Threshold matches useBreakpoint's `lg` (min-width: 1024px).
const isDesktopViewport = (): boolean =>
  typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches

const DealContext = createContext<DealContextType | undefined>(undefined)

export const useDealContext = () => {
  const context = useContext(DealContext)
  if (!context) {
    throw new Error('useDealContext must be used within a DealContextProvider')
  }
  return context
}

type DealContextProviderProps = {
  children: ReactNode
}

export const DealContextProvider: FC<DealContextProviderProps> = ({ children }) => {
  const [claimed, setClaimed] = useState<boolean>(false)
  const [triggerClaim, setTriggerClaim] = useState<boolean>(false)
  const [dealSelectionComplete, setDealSelectionComplete] = useState<boolean>(false)
  const [response, setResponse] = useState<'error' | null>(null)
  const [email, setEmail] = useState<string>('')
  const [dealId, setDealId] = useState<number | null>(null)
  const [appName, setAppName] = useState<string>('')
  const [claimValueCents, setClaimValueCents] = useState<number | null>(null)
  const [emailIsRequired, setEmailIsRequired] = useState<boolean>(false)
  const [desktopIsRequired, setDesktopIsRequired] = useState<boolean>(false)
  const [showDesktopSheet, setShowDesktopSheet] = useState<boolean>(false)
  const [dealTracked, setDealTracked] = useState<boolean>(false)
  const [signIn, setSignIn] = useState<boolean>(false)

  // Reactive lg (>= 1024px) is used only to auto-close the desktop handoff sheet
  // when a tablet is resized up to desktop. The one-shot claim gate does NOT use
  // this — it reads the viewport synchronously via isDesktopViewport() to avoid
  // the useSyncExternalStore first-render race (see that helper's comment).
  const { lg } = useBreakpoint()

  const emailInputRef = useRef<{ focus: () => void }>(null)

  const handleClick = async () => {
    await scrollToSection({ id: 'top', offset: 0, animate: true, hideHash: true })

    setTimeout(() => {
      setTriggerClaim(true)
    }, 500)
  }

  // Returns whether the claim actually completed (vs. deferred to sign-in or
  // a no-op button-only call) — callers use this to know whether it's safe
  // to clear claim-related localStorage state, since an emailRequired claim
  // that triggers sign-in needs that state to survive the redirect round-trip.
  //
  // `overrides` lets a caller pass the resolved deal/app metadata directly
  // instead of relying on dealId/appName/claimValueCents from context state.
  // DealClaim's own sync effect (keyed on its `id` prop) is what normally
  // keeps that state current for the button-click path, but a caller that
  // resolves and claims a deal within the same effect (e.g. DealContainer's
  // URL/localStorage-driven auto-claim) can run before that sync effect has
  // committed, so context state may still reflect the previous deal.
  const handleClaim = useCallback(
    async (
      buttonOnly?: boolean,
      emailRequired?: boolean | null | undefined,
      desktopIsRequired?: boolean | null | undefined,
      overrides?: DealClaimOverrides,
    ): Promise<boolean> => {
      if (buttonOnly) {
        handleClick()
        return false
      }

      const resolvedDealId = overrides && 'dealId' in overrides ? overrides.dealId : dealId
      const resolvedAppName = overrides?.appName ?? appName
      const resolvedClaimValueCents =
        overrides && 'claimValueCents' in overrides ? overrides.claimValueCents : claimValueCents

      if (desktopIsRequired && !isDesktopViewport()) {
        setShowDesktopSheet(true)
        return false
      }

      if (emailRequired) {
        // Membership is resolved here (not passed in) so every claim path —
        // button click, triggerClaim effect, ?claim=true return from login —
        // gets the same check. refresh: true so a login completed in another
        // tab (magic link) is picked up on the next claim attempt without a
        // reload; claim clicks are rare enough that re-fetching is free.
        const user = await fetchCurrentUser({ refresh: true })

        if (!user) {
          if (resolvedDealId) {
            try {
              localStorage.setItem('efficient:deal_id', String(resolvedDealId))
              localStorage.setItem('efficient:claim', 'true')
            } catch {
              // ignore storage failures; still route to sign-in
            }
          }
          setSignIn(true)
          return false
        }

        // Submit with the logged-in user's account email so the Loops
        // transactional email flow fires exactly as the old email-input flow did
        submitForm({
          type: 'deal',
          email: user.email,
          pageUrl: window.location.href,
          dealId: resolvedDealId ?? undefined,
        }).catch(() => {
          // Fire-and-forget: don't block the claim UI on submission failure
        })

        trackFormDealClaim(resolvedAppName, resolvedClaimValueCents ?? undefined)
      }

      setClaimed(true)
      hapticTap(40)
      return true
    },
    [dealId, appName, claimValueCents, setClaimed, setSignIn, setShowDesktopSheet],
  )

  useEffect(() => {
    if (triggerClaim && dealSelectionComplete) {
      handleClaim(false, emailIsRequired, desktopIsRequired)
      setTriggerClaim(false)
    }
  }, [triggerClaim, dealSelectionComplete, handleClaim, emailIsRequired, desktopIsRequired])

  useEffect(() => {
    if (lg) {
      setShowDesktopSheet(false)
    }
  }, [lg])

  return (
    <DealContext.Provider
      value={{
        claimed,
        setClaimed,
        triggerClaim,
        setTriggerClaim,
        dealSelectionComplete,
        setDealSelectionComplete,
        emailInputRef,
        email,
        setEmail,
        dealId,
        setDealId,
        appName,
        setAppName,
        claimValueCents,
        setClaimValueCents,
        emailIsRequired,
        setEmailIsRequired,
        desktopIsRequired,
        setDesktopIsRequired,
        showDesktopSheet,
        setShowDesktopSheet,
        dealTracked,
        setDealTracked,
        signIn,
        setSignIn,
        response,
        handleClaim,
        handleClick,
      }}
    >
      {children}
    </DealContext.Provider>
  )
}
